import React, { useEffect, useState } from "react";
import styles from "./manage-promotion-drawer.module.css";
import CustomDrawer from "../../../../../common/custom-drawer/custom-drawer";
import CustomButton from "../../../../../common/custom-button/custom-button";
import { Select, Spin } from "antd";
import CustomSelect from "../../../../../common/input/custom-select/custom-select";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { promoteStudents } from "../../../../../services/student_promotion";
import { PROMOTION_RULES } from "../../../../../constants/common";
import AutoRuleFields from "../auto-rule-fields/auto-rule-fields";
import SubjectThresholdFields from "../subject-threshold-fields/subject-threshold-fields";
import RankingPositionFields from "../ranking-position-fields/ranking-position-fields";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../../../../redux/features/globalLoaderSlice";
import usePromotionPolling from "../../../../../hooks/usePromotionPolling";

const RULE_CONFIG = {
  "auto": {
    label: "Auto Promotion",
    component: AutoRuleFields,
  },
  "batch-with-ranking": {
    label: "Using Student Positions",
    component: RankingPositionFields,
  },
  "batch-with-subject-threshold": {
    label: "Using Subject Threshold",
    component: SubjectThresholdFields,
  },
};

const defaultInputValues = {
  ruleType: "",
  sourceClassroomId: "",
  targetClassroomId: "",
  exam_type_id: "",
  exam_id: "",
  subject_id: "",
  min_value: "",
  max_value: "",
};

const ManagePromotionDrawer = ({ open, onClose, record }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { startPolling } = usePromotionPolling({
    onComplete: onClose,
  });
  const { control, getValues, reset, trigger, watch } = useForm({
    mode: "onChange",
    defaultValues: defaultInputValues,
  });
  const ruleType = watch("ruleType");
  const SelectedRuleComponent = RULE_CONFIG[ruleType]?.component || null;

  useEffect(() => {
    reset({ ...defaultInputValues, ruleType });
  }, [ruleType, reset]);

  const handleSubmit = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }

      const formValues = getValues();
      const payload = { type: formValues?.ruleType }
      
      switch (formValues.ruleType) {
        case "auto":
          payload.sourceClassroomId = formValues?.sourceClassroomId;
          payload.targetClassroomId = formValues?.targetClassroomId;
          break;
        case "batch-with-ranking":
          payload.sourceLevelId = record?.current_level?.id;
          payload.targetLevelId = record?.next_level?.id;
          payload.exam_id = formValues?.exam_id;
          break;
        case "batch-with-subject-threshold":
          payload.sourceLevelId = record?.current_level?.id;
          payload.targetLevelId = record?.next_level?.id;
          payload.exam_id = formValues?.exam_id;
          payload.subject_id = formValues?.subject_id;
          payload.min = formValues?.min_value;
          payload.max = formValues?.max_value;
          break;
        default:
          break;
      }
      dispatch(showLoader("Student promotion in progress..."));
      const response = await promoteStudents(payload);
      const jobId = response?.data?.data?.jobId;
      startPolling(jobId);
    } catch (err) {
      console.log(err);
      dispatch(hideLoader());
      toast.error(err?.data?.message || "Failed to promote students.");
    }
  };

  const handleClose = () => {
    reset(defaultInputValues);
    onClose();
  };

  return (
    <CustomDrawer
      title="Promote Students"
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title="Promote Students"
            size="large"
            onClick={handleSubmit}
            disabled={!record?.current_level?.total_students || !record?.next_level}
          />
        </div>
      }
      width={500}
      drawerStyles={{ body: { padding: "32px" } }}
    >
      <Spin spinning={loading}>
        <div className={styles.promotionContainer}>
          <div className={styles.levelFlowCard}>
            <div className={styles.levelItem}>
              <div className={styles.levelLabel}>From</div>
              <div className={styles.levelName}>{record?.current_level?.name}</div>
            </div>
            <div className={styles.levelArrow}>→</div>
            <div className={styles.levelItem}>
              <div className={styles.levelLabel}>To</div>
              <div className={styles.levelName}>{record?.next_level?.name}</div>
            </div>
          </div>

          <Controller
            name="ruleType"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomSelect
                id="ruleType"
                label="Promotion Type"
                showRequired
                labelColor="#667085"
                placeholder="--Select--"
                showSearch
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              >
                {PROMOTION_RULES.map((option) => (
                  <Select.Option
                    key={option?.value}
                    value={option?.value}
                  >
                    {option?.label}
                  </Select.Option>
                ))}
              </CustomSelect>
            )}
          />

          {SelectedRuleComponent && (
            <SelectedRuleComponent
              control={control}
              getValues={getValues}
              watch={watch}
              record={record}
              setLoading={setLoading}
            />
          )}
        </div>
      </Spin>
    </CustomDrawer>
  );
};

export default ManagePromotionDrawer;
